/**
 * LES PHRASES QUI DISENT UN FAIT — la règle, écrite une fois (lot 1.6).
 *
 * ─── Ce que ce fichier corrige ─────────────────────────────────────────────
 *
 * Le lot 1.3 a donné UN propriétaire à chaque fait partagé : « quand », « combien
 * de fois », « combien d'observations », « quel palier ». Le nombre est donc
 * juste partout. La PHRASE qui l'entoure, elle, était encore écrite par chaque
 * écran :
 *
 *  - la fiche parent (mobile) composait « 5 jours de pratique sur les sept
 *    derniers jours. », avec sa propre branche pour zéro ;
 *  - la console tuteur composait « sans séance depuis 9 jours » de son côté ;
 *  - la fiche tuteur (mobile) écrivait « 1 séance » sous « Aides aux devoirs »,
 *    puis « N aides aux devoirs » après correction — deux formulations pour un
 *    même compteur, à deux endroits ;
 *  - deux écrans annonçaient « N savoirs observés » là où le chiffre compte des
 *    mesures.
 *
 * C'est exactement le mécanisme par lequel les quatre définitions de « séance »
 * sont nées : chaque surface, de bonne foi, à un moment différent. Une phrase
 * dupliquée finit toujours par diverger, et une divergence de phrase se lit
 * comme une divergence de fait.
 *
 * ─── La règle ──────────────────────────────────────────────────────────────
 *
 * > Le serveur possède tout texte rendu au parent et au tuteur. Quand une
 * > phrase est composée à partir d'un nombre, la RÈGLE de cette phrase vit
 * > ici — jamais dans un écran, jamais deux fois.
 *
 * Ce module est PUR : ni DOM, ni réseau, ni base. Il est consommé par le
 * serveur (qui rend les documents et les verdicts) et par les deux clients pour
 * les libellés qui accompagnent un chiffre déjà servi.
 */
import { denombrer, pluriel } from './accord.js';
import { nommerJoursDePratique } from './vocabulaire.js';
/**
 * « Cinq jours de pratique sur les sept derniers jours. » — ou la phrase de
 * l'absence, qui NOMME les trois sources plutôt que d'écrire « zéro ».
 *
 * Dire « zéro jour de pratique » à un parent est exact et inutilisable :
 * il ne sait pas ce qui a été regardé. La phrase de l'absence énumère donc les
 * trois traces qu'on a cherchées — ce sont précisément les trois sources de
 * `lib/activite-eleve.ts`, et les nommer est ce qui rend le fait vérifiable.
 */
export function phraseJoursDePratique(nbJours) {
    if (nbJours <= 0) {
        return 'Aucune séance, aucun exercice, aucune aide aux devoirs cette semaine.';
    }
    return `${nommerJoursDePratique(nbJours)} sur les sept derniers jours.`;
}
/**
 * L'ancienneté de la dernière séance, telle qu'un adulte la lit d'un coup
 * d'œil dans une liste : « séance aujourd'hui », « séance hier », « sans séance
 * depuis 9 jours ».
 *
 * `null` — l'élève n'a jamais travaillé — ne rend PAS un « jamais » chiffré :
 * on ne fabrique pas une durée qu'on n'a pas mesurée.
 */
export function phraseDerniereSeance(joursSansSeance) {
    if (joursSansSeance === null)
        return 'aucune séance à ce jour';
    if (joursSansSeance <= 0)
        return "séance aujourd'hui";
    if (joursSansSeance === 1)
        return 'séance hier';
    return `sans séance depuis ${denombrer(joursSansSeance, 'jour')}`;
}
/**
 * « 3 aides aux devoirs » — le compteur de la fiche tuteur.
 *
 * Il écrivait « 1 séance » (BD-03) : un mot appliqué à un autre objet, sous un
 * titre « Aides aux devoirs », pendant que le parent lisait « cinq séances »
 * le même jour. Le nom du fait est ici, et il n'est plus recopié.
 */
export function nommerAidesDevoirs(nb) {
    return denombrer(nb, 'aide aux devoirs', 'aides aux devoirs');
}
/** L'absence, en toutes lettres — jamais « 0 aide aux devoirs ». */
export const AUCUNE_AIDE_DEVOIRS = 'Aucune aide aux devoirs enregistrée.';
/**
 * « 7 savoirs observés · 9 mesures » — et « 7 savoirs observés » tout court
 * quand les deux nombres coïncident.
 *
 * `MaitriseSavoir` porte une ligne par (savoir, dimension) : un savoir visuel
 * se mesure sur deux choses. Compter les lignes et les appeler des savoirs
 * était le défaut BD-04 — « 9 savoirs du programme observés » chez Léa, pour
 * sept savoirs distincts. Les deux nombres existent ; la phrase les distingue,
 * et ne les divise jamais.
 */
export function phraseSavoirsObserves(nbSavoirs, nbMesures) {
    const savoirs = `${denombrer(nbSavoirs, 'savoir')} ${pluriel(nbSavoirs, 'observé')}`;
    if (nbMesures === nbSavoirs)
        return savoirs;
    return `${savoirs} · ${denombrer(nbMesures, 'mesure')}`;
}
//# sourceMappingURL=faits.js.map